import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, Menu, X, Check, Sparkles, Crown, ArrowRight,
  HelpCircle, Zap, FileBarChart2, Radar, BellRing, Layers, Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import LegalFooter from "@/components/legal/LegalFooter";
import { api } from "@/lib/api";

// Display-only plan metadata. Pricing + feature bullets come from
// `/api/billing/plans` so the public Pricing page can never drift from
// the in-app Billing page / backend catalog.
const PLAN_META = {
  free: {
    blurb: "For solo operators kicking the tires.",
    cta: "Start free",
    icon: ShieldCheck,
  },
  pro: {
    blurb: "For growing SMBs who need confidence.",
    featured: true,
    badge: "Most popular",
    cta: "Start free — upgrade anytime",
    icon: Sparkles,
  },
  business: {
    blurb: "For teams that scale audit & compliance ops.",
    badge: "For teams",
    cta: "Start free — upgrade anytime",
    icon: Crown,
  },
};

const PLAN_ORDER = ["free", "pro", "business"];

const FEATURE_MATRIX = [
  { row: "Document scans / month", icon: Zap, free: "5", pro: "100", business: "1,000" },
  { row: "Workspaces", icon: Layers, free: "1", pro: "3", business: "Unlimited" },
  { row: "Multilingual scanning", icon: Sparkles, free: true, pro: true, business: true },
  { row: "Suggested redline (Word tracked changes)", icon: FileBarChart2, free: "Markdown", pro: true, business: true },
  { row: "AI chat on reports", icon: Sparkles, free: false, pro: true, business: true },
  { row: "Compliance Scorecard PDF", icon: FileBarChart2, free: false, pro: true, business: true },
  { row: "Public share links with email-gate", icon: BellRing, free: false, pro: true, business: true },
  { row: "Version history & risk diff", icon: Layers, free: true, pro: true, business: true },
  { row: "Head-to-head comparison", icon: Layers, free: true, pro: true, business: true },
  { row: "PDF audit reports", icon: FileBarChart2, free: false, pro: true, business: true },
  { row: "Monitoring jobs", icon: Radar, free: false, pro: true, business: true },
  { row: "Email + dashboard alerts", icon: BellRing, free: "Weekly digest", pro: true, business: true },
  { row: "Slack integration", icon: BellRing, free: false, pro: false, business: true },
  { row: "Google Drive import", icon: Layers, free: false, pro: false, business: true },
  { row: "Recipients dashboard", icon: BellRing, free: false, pro: false, business: true },
  { row: "Priority support (SLA)", icon: HelpCircle, free: false, pro: "Email", business: "Email + SLA" },
];

const FAQ = [
  {
    q: "Do you offer a free tier?",
    a: "Yes — the Free plan gives you 5 document scans per month forever. No credit card required to sign up.",
  },
  {
    q: "Can I switch plans or cancel anytime?",
    a: "Of course. Upgrade or downgrade from your Billing settings in one click. Cancellations take effect at the end of the current paid period and you keep access through that period.",
  },
  {
    q: "How does the annual discount work?",
    a: "Pay for 10 months and get 12 — roughly 17% off. You can switch between monthly and annual at any time.",
  },
  {
    q: "Is there a refund policy?",
    a: "Yes — a 14-day money-back guarantee on every new paid subscription. See our Refund Policy for the details.",
  },
  {
    q: "Which payment methods do you accept?",
    a: "All major credit and debit cards (Visa, Mastercard, Amex, Discover) and several local methods via our payment processor.",
  },
  {
    q: "Is AgentFlow legal advice?",
    a: "No. AgentFlow Compliance is an informational tool to surface potential risks in your business documents. It does not replace advice from a qualified legal professional. Always consult counsel for binding legal opinions.",
  },
  {
    q: "What languages do you support?",
    a: "Five today: English, Spanish, Portuguese, French, and German. We auto-detect the contract's language and cite the right legal framework for that jurisdiction (LOPDGDD/RGPD for Spain, LGPD for Brazil/Portugal, CNIL/RGPD for France, DSGVO for Germany, GDPR/CCPA/HIPAA for English). Toggle between the native report and an English translation with one click.",
  },
  {
    q: "Are Slack and Google Drive really live?",
    a: "Yes. Both are working today on Business. Google Drive imports PDFs / Word / Google Docs via the official Picker (we only see files you explicitly select). Slack posts critical and high-risk scan alerts plus an optional daily digest to a channel of your choice.",
  },
];

export default function PublicPricing() {
  const [cycle, setCycle] = useState("monthly");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [plans, setPlans] = useState(null);   // null = loading, [] = error
  const [loadError, setLoadError] = useState(false);

  // Scroll to top on mount
  useEffect(() => { window.scrollTo(0, 0); }, []);

  // Fetch plan catalog so PublicPricing shares one source of truth with the
  // in-app Billing page and the backend `PLANS` dict.
  useEffect(() => {
    let cancelled = false;
    api.get("/billing/plans")
      .then(({ data }) => {
        if (cancelled) return;
        // Order plans deterministically (free → pro → business), merge with
        // display-only metadata, drop anything we don't render copy for.
        const byId = {};
        (data.plans || []).forEach((p) => { byId[p.id] = p; });
        const merged = PLAN_ORDER
          .filter((id) => byId[id] && PLAN_META[id])
          .map((id) => ({
            id,
            name: byId[id].name,
            monthly: byId[id].price_monthly_usd,
            annual: byId[id].price_annual_usd,
            features: byId[id].features || [],
            ...PLAN_META[id],
          }));
        setPlans(merged);
      })
      .catch(() => {
        if (cancelled) return;
        setLoadError(true);
        setPlans([]);
      });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-white text-slate-900" data-testid="public-pricing-page">
      {/* Top nav */}
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-md border-b border-slate-200/70">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group">
            <div className="h-8 w-8 rounded-lg bg-blue-600 flex items-center justify-center">
              <ShieldCheck className="h-5 w-5 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-display text-lg font-semibold tracking-tight">
              AgentFlow<span className="text-blue-600">.</span>
            </span>
          </Link>
          <nav className="hidden md:flex items-center gap-7 text-sm">
            <Link to="/" className="text-slate-600 hover:text-slate-900 transition">Home</Link>
            <Link to="/pricing" className="text-slate-900 font-medium">Pricing</Link>
            <Link to="/terms" className="text-slate-600 hover:text-slate-900 transition">Terms</Link>
            <Link to="/privacy" className="text-slate-600 hover:text-slate-900 transition">Privacy</Link>
          </nav>
          <div className="hidden md:flex items-center gap-3">
            <Link to="/login" className="text-sm font-medium text-slate-700 hover:text-slate-900 transition">Sign in</Link>
            <Link to="/register">
              <Button className="rounded-lg bg-blue-600 hover:bg-blue-700 text-white">Start Free</Button>
            </Link>
          </div>
          <button onClick={() => setMobileOpen(!mobileOpen)} className="md:hidden p-2 -mr-2" aria-label="Toggle menu">
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
        {mobileOpen && (
          <div className="md:hidden border-t border-slate-200 bg-white">
            <div className="px-6 py-4 flex flex-col gap-3 text-sm">
              <Link to="/" onClick={() => setMobileOpen(false)} className="text-slate-700">Home</Link>
              <Link to="/pricing" onClick={() => setMobileOpen(false)} className="text-slate-900 font-medium">Pricing</Link>
              <Link to="/terms" onClick={() => setMobileOpen(false)} className="text-slate-700">Terms</Link>
              <Link to="/privacy" onClick={() => setMobileOpen(false)} className="text-slate-700">Privacy</Link>
              <Link to="/login" onClick={() => setMobileOpen(false)} className="text-slate-700">Sign in</Link>
              <Link to="/register" onClick={() => setMobileOpen(false)}>
                <Button className="w-full rounded-lg bg-blue-600 hover:bg-blue-700 text-white">Start Free</Button>
              </Link>
            </div>
          </div>
        )}
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-b from-slate-50 to-white border-b border-slate-200/70">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-14 lg:py-20 text-center">
          <p className="text-xs uppercase tracking-[0.22em] font-semibold text-blue-600">Pricing</p>
          <h1 className="mt-3 font-display font-semibold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-slate-900 leading-tight">
            Simple plans that scale with your{" "}
            <span className="text-blue-600">compliance volume.</span>
          </h1>
          <p className="mt-4 text-base lg:text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
            Start free — no credit card required. Upgrade to Pro or Business
            anytime from your dashboard. 14-day money-back guarantee on every
            paid plan. Cancel anytime.
          </p>

          {/* Cycle toggle */}
          <div className="mt-10 inline-flex rounded-full border border-slate-200 bg-white p-1 shadow-sm" data-testid="public-cycle-toggle">
            {["monthly", "annual"].map((c) => (
              <button
                key={c}
                onClick={() => setCycle(c)}
                data-testid={`public-cycle-${c}`}
                className={
                  "px-5 py-2 rounded-full text-xs font-semibold uppercase tracking-wider transition " +
                  (cycle === c ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900")
                }
              >
                {c === "monthly" ? "Monthly" : "Annual"}
                {c === "annual" && (
                  <span className="ml-2 inline-block px-1.5 py-0.5 rounded-full bg-emerald-500 text-white text-[9px] font-bold tracking-normal normal-case">
                    Save 17%
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Plan cards */}
      <section className="py-14 lg:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {plans === null && (
            <div className="flex justify-center py-16" data-testid="public-pricing-loading">
              <Loader2 className="h-7 w-7 animate-spin text-blue-600" />
            </div>
          )}
          {plans && plans.length === 0 && loadError && (
            <div className="text-center py-12 text-sm text-slate-600" data-testid="public-pricing-error">
              We couldn't load live pricing right now. Please refresh in a moment or{" "}
              <Link to="/register" className="text-blue-600 underline">start free</Link> in the meantime.
            </div>
          )}
          {plans && plans.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6">
            {plans.map((p) => {
              const price = cycle === "annual" ? p.annual : p.monthly;
              const featured = p.featured;
              const Icon = p.icon;
              return (
                <div
                  key={p.id}
                  data-testid={`public-plan-${p.id}`}
                  className={
                    "rounded-2xl border p-7 shadow-sm relative flex flex-col " +
                    (featured
                      ? "border-slate-900 bg-slate-900 text-white shadow-lg lg:scale-[1.03]"
                      : "border-slate-200 bg-white")
                  }
                >
                  {featured && (
                    <span className="absolute -top-3 right-6 rounded-full bg-emerald-500 text-white text-[10px] font-bold uppercase tracking-[0.15em] px-2.5 py-1">
                      Most popular
                    </span>
                  )}
                  {p.badge && !featured && (
                    <span className="absolute -top-3 right-6 rounded-full bg-blue-50 text-blue-700 border border-blue-200 text-[10px] font-bold uppercase tracking-[0.15em] px-2.5 py-1" data-testid={`public-plan-${p.id}-badge`}>
                      Beta · {p.badge}
                    </span>
                  )}
                  {p.badge && featured && (
                    <span className="absolute -top-3 left-6 rounded-full bg-amber-400 text-slate-900 text-[10px] font-bold uppercase tracking-[0.15em] px-2.5 py-1" data-testid={`public-plan-${p.id}-badge`}>
                      Beta · {p.badge}
                    </span>
                  )}
                  <div className="flex items-center gap-2">
                    <Icon className={"h-5 w-5 " + (featured ? "text-amber-400" : "text-slate-700")} />
                    <div className={"font-display text-xl font-semibold " + (featured ? "text-white" : "text-slate-900")}>
                      {p.name}
                    </div>
                  </div>
                  <p className={"mt-2 text-sm " + (featured ? "text-slate-300" : "text-slate-600")}>
                    {p.blurb}
                  </p>
                  <div className="mt-5 flex items-baseline gap-1">
                    <div className={"font-display text-5xl font-semibold " + (featured ? "text-white" : "text-slate-900")}>
                      ${price}
                    </div>
                    <div className={"text-sm " + (featured ? "text-slate-400" : "text-slate-500")}>
                      {cycle === "annual" ? "/yr" : "/mo"}
                    </div>
                  </div>
                  {cycle === "annual" && p.id !== "free" && (
                    <div className={"text-xs mt-1 font-medium " + (featured ? "text-emerald-300" : "text-emerald-600")}>
                      Save ${(p.monthly * 12 - p.annual)} vs monthly
                    </div>
                  )}
                  <ul className={"mt-6 space-y-2.5 text-sm flex-1 " + (featured ? "text-slate-200" : "text-slate-700")}>
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5">
                        <Check className={"h-4 w-4 mt-0.5 shrink-0 " + (featured ? "text-emerald-400" : "text-emerald-600")} />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/register" className="block mt-7">
                    <Button
                      data-testid={`public-cta-${p.id}`}
                      className={
                        "w-full rounded-lg " +
                        (featured
                          ? "bg-emerald-500 hover:bg-emerald-600 text-white"
                          : "bg-blue-600 hover:bg-blue-700 text-white")
                      }
                    >
                      {p.cta} <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              );
            })}
          </div>
          )}

          {plans && plans.length > 0 && (
            <p className="mt-8 text-center text-xs text-slate-500">
              Prices in USD. Tax calculated at checkout. Secure payments via our payment processor.
            </p>
          )}
        </div>
      </section>

      {/* Feature comparison */}
      <section className="bg-slate-50 border-y border-slate-200 py-14 lg:py-20">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">
            Compare features
          </h2>
          <p className="mt-2 text-sm text-slate-600">Everything you get on each plan.</p>

          <div className="mt-8 overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-5 py-4 text-left text-xs uppercase tracking-[0.12em] font-semibold text-slate-500">Feature</th>
                  <th className="px-5 py-4 text-center text-xs uppercase tracking-[0.12em] font-semibold text-slate-500">Free</th>
                  <th className="px-5 py-4 text-center text-xs uppercase tracking-[0.12em] font-semibold text-blue-700 bg-blue-50/50">Pro</th>
                  <th className="px-5 py-4 text-center text-xs uppercase tracking-[0.12em] font-semibold text-slate-500">Business</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {FEATURE_MATRIX.map((r) => (
                  <tr key={r.row}>
                    <td className="px-5 py-4 text-slate-900 font-medium">{r.row}</td>
                    <Cell value={r.free} />
                    <Cell value={r.pro} highlight />
                    <Cell value={r.business} />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 lg:py-20">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <h2 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900">
            Pricing FAQ
          </h2>
          <Accordion type="single" collapsible className="mt-8 divide-y divide-slate-200">
            {FAQ.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="py-1">
                <AccordionTrigger className="text-left font-medium text-slate-900 hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-slate-900 text-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 py-14 lg:py-20 text-center">
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight">
            Start scanning in under 2 minutes.
          </h2>
          <p className="mt-3 text-sm sm:text-base text-slate-300 max-w-2xl mx-auto">
            No credit card required for the Free plan. Upgrade when you need to.
          </p>
          <div className="mt-7">
            <Link to="/register">
              <Button className="rounded-lg bg-blue-600 hover:bg-blue-700 text-white px-7 h-11">
                Get started free <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <LegalFooter />
    </div>
  );
}

function Cell({ value, highlight }) {
  let content;
  if (value === true) content = <Check className="h-4 w-4 text-emerald-600 inline" />;
  else if (value === false) content = <span className="text-slate-300">—</span>;
  else content = <span className="text-slate-700">{value}</span>;
  return (
    <td className={"px-5 py-4 text-center text-sm " + (highlight ? "bg-blue-50/40 font-medium" : "")}>
      {content}
    </td>
  );
}
